import { css } from "@emotion/react";
import type { ProReview } from "@swo/storefront/dist-types/services/page-data-service/data/pages/product-detail-page/pro-reviews/interfaces";
import type React from "react";
import { useState } from "react";
import { ContentfulImage2 } from "../../components/_shared-components/image-related/ContentfulImage2";
import { SORichTextRenderer } from "../../components/_shared-components/so-rich-text";
import { usePageLocaleContext } from "../../global/page-context";
import { FONTS, fontSize } from "../../styles/fonts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { ReviewMentionedProducts } from "./ReviewMentionedProducts";
import { ReviewTranslatedFrom } from "./ReviewTranslatedFrom";
import type { ReviewTranslations } from "./reviewTranslations";
import { richTextOptions } from "./rich-text-options";
import { CSS_MQ } from "../../styles/breakpoints";
import useMeasure from "react-use-measure";
import type { TranslatedValue } from "@swo/storefront";

export const ProReviewCard: React.FC<{
  item: ProReview;
  headline: TranslatedValue;
  isActive?: boolean;
  t: ReviewTranslations;
  ref?: any;
  minHeight: number;
}> = ({ item, headline, isActive, t, ref, minHeight }) => {
  const { locale } = usePageLocaleContext();

  const [ref1, bounds1] = useMeasure();
  const [ref2, bounds2] = useMeasure();

  const maxHeight = Math.max(bounds1.height, bounds2.height);

  const { review, mainVisual, referencedProducts, additionalText, author } = item;

  const isOriginalLanguageDifferentFromCurrentLanguage = review.originalLanguageIsoCode !== locale.language.isoCode;

  const [showingReview, setShowingReview] = useState<"localised" | "original">(
    isOriginalLanguageDifferentFromCurrentLanguage ? "localised" : "original",
  );

  return (
    <div
      ref={ref}
      className={isActive ? "active" : "inactive"}
      style={{ "--min-height": `${minHeight}px` } as any}
      css={css`
        display: grid;
        flex-grow: 1;

        ${CSS_MQ.atMostSm} {
          grid-template-columns: 1fr;
          grid-template-rows: auto 1fr auto auto;

          grid-template-areas:
            "image"
            "review"
            "products"
            "translated";
        }

        ${CSS_MQ.atLeastMd} {
          grid-template-columns: 1fr 180px;
          grid-template-rows: auto;
        }
        ${CSS_MQ.atLeastLg} {
          grid-template-columns: 1fr auto;
        }

        min-height: var(--min-height);

        background-color: ${CSS_COLORS.GreenWhite};

        transition: filter 0.2s linear;

        &.active {
          filter: brightness(1);
        }

        &.inactive {
          filter: brightness(0.95);

          &:hover {
            filter: brightness(1);
          }
        }

        gap: 24px;
        padding: 24px;
        ${CSS_MQ.atLeastMd} {
          padding: 32px 40px;
          gap: 32px;
        }
        ${CSS_MQ.atLeastLg} {
          padding: 50px 88px;
          gap: 50px;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          color: ${CSS_COLORS.Black};
          text-align: left;

          ${CSS_MQ.atMostSm} {
            display: contents;
          }
        `}
      >
        <div
          css={css`
            overflow-y: hidden;

            position: relative;

            > * {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
            }
          `}
          style={{ height: `${maxHeight}px` }}
        >
          <div className={showingReview === "localised" ? undefined : "u-hidden"} ref={ref1}>
            <SORichTextRenderer richText={review.localised} options={richTextOptions} />
          </div>
          <div className={showingReview === "original" ? undefined : "u-hidden"} ref={ref2}>
            <SORichTextRenderer richText={review.original} options={richTextOptions} />
          </div>
        </div>

        <ReviewMentionedProducts referencedProducts={referencedProducts} headline={headline} />
      </div>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          ${CSS_MQ.atMostSm} {
            display: contents;
          }
        `}
      >
        <ReviewImageWithText mainVisual={mainVisual} author={author} additionalText={additionalText} />

        {isOriginalLanguageDifferentFromCurrentLanguage && (
          <div
            css={css`
              color: ${CSS_COLORS.Black};
              text-align: left;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: flex-end;

              ${CSS_MQ.atLeastMd} {
                margin-top: 24px;
              }

              ${CSS_MQ.atMostSm} {
                grid-area: translated;
              }
            `}
          >
            <hr
              css={css`
                width: 20px;
                height: 1px;
                margin-bottom: 20px;
                margin-top: 0;
                margin-left: 0;
                margin-right: 0;

                background-color: ${CSS_COLORS.Black};

                border: none;
                padding: 0;
                outline: none;

                display: none;
                ${CSS_MQ.atLeastMd} {
                  display: block;
                }
              `}
            />

            <ReviewTranslatedFrom
              t={t}
              locale={locale}
              languageIsoCode={review.originalLanguageIsoCode}
              css={
                showingReview !== "localised"
                  ? css`
                      visibility: hidden;
                    `
                  : undefined
              }
            />

            <button
              onClick={() => setShowingReview(s => (s === "original" ? "localised" : "original"))}
              css={[
                FONTS.SwarovskiFutura300,
                fontSize.x2,
                css`
                  text-decoration: underline;
                `,
              ]}
            >
              {showingReview === "original" ? "View localised review" : "View original review"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewImageWithText: React.FC<{
  mainVisual: ProReview["mainVisual"];
  author: ProReview["author"];
  additionalText: ProReview["additionalText"];
}> = ({ mainVisual, author, additionalText }) => {
  return (
    <div
      css={css`
        ${CSS_MQ.atMostSm} {
          grid-area: image;
          grid-template-columns: auto 1fr;
          display: grid;
          gap: 16px;
        }
      `}
    >
      <div
        css={css`
          aspect-ratio: 1/1;

          display: flex;
          width: 64px;

          ${CSS_MQ.atLeastMd} {
            width: 180px;
          }

          ${CSS_MQ.atLeastLg} {
            width: 260px;
          }
        `}
      >
        <ContentfulImage2
          assetId={mainVisual?.image.sys.id}
          contentfulUrl={mainVisual?.image.url}
          cropping={mainVisual ?? undefined}
          withTransparency={true}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;

            ${CSS_MQ.atMostSm} {
              border-radius: 100%;
            }
          `}
          sizes="(max-width: 768px) 64px, (max-width: 992px) 180px, 260px"
        />
      </div>

      <div
        css={[
          fontSize.x25,
          FONTS.SwarovskiFutura500,
          css`
            color: ${CSS_COLORS.Black};

            ${CSS_MQ.atMostSm} {
              align-self: center;
            }

            ${CSS_MQ.atLeastMd} {
              margin-top: 20px;
            }

            display: flex;
            flex-direction: column;
          `,
        ]}
      >
        {author && <span>{author}</span>}
        {additionalText && <span>{additionalText}</span>}
      </div>
    </div>
  );
};
